import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../shared/api/client";
import type {
  EmployeeSummary,
  EnvironmentView,
  PageResponse,
  Priority,
  ReleaseDetail,
  ReleaseType,
} from "../../shared/types/api";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Input,
  Select,
  Textarea,
} from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";
import { RELEASE_TYPE_LABELS } from "./releaseVocabulary";

const TYPES: ReleaseType[] = [
  "MAJOR", "MINOR", "PATCH", "HOTFIX", "EMERGENCY",
  "UPGRADE", "INFRASTRUCTURE", "CONFIGURATION",
];

const PRIORITIES: [Priority, string][] = [
  ["CRITICAL", "Critical"],
  ["HIGH", "High"],
  ["MEDIUM", "Medium"],
  ["LOW", "Low"],
];

/** The five named seats a release notifies, in the order the Overview lists them. */
const PEOPLE = [
  ["releaseOwnerId", "Release owner", "Accountable for this release overall."],
  ["releaseManagerId", "Release manager", "Runs the process."],
  ["technicalLeadId", "Technical lead", "Answers for the code and the design."],
  ["qaLeadId", "QA lead", "Answers for testing."],
  ["deploymentOwnerId", "Deployment owner", "Answers for getting it out."],
] as const;

type PeopleKey = (typeof PEOPLE)[number][0];

/**
 * Everything about a release that is not a governed act (§18).
 *
 * <p>The dividing line is worth stating. Freezing scope, approving, signing off and moving
 * through the workflow all have their own screens because each is a decision with a record. What
 * is left — who owns it, where it is going, what it is for, which branch it came from — is
 * description, and description belongs in one form rather than five.
 *
 * <p>The target environment is here rather than only at creation because it genuinely changes:
 * a release aimed at staging gets promoted, and the thing it is judged against has to move with
 * it. Changing it re-judges the release immediately, which is the point.
 */
export function ReleaseEditDrawer({
  release,
  open,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [releaseName, setReleaseName] = useState("");
  const [version, setVersion] = useState("");
  const [releaseType, setReleaseType] = useState<ReleaseType>("MINOR");
  const [environmentId, setEnvironmentId] = useState("");
  const [riskLevel, setRiskLevel] = useState<Priority>("MEDIUM");
  const [priority, setPriority] = useState<Priority>("MEDIUM");
  const [people, setPeople] = useState<Record<string, string>>({});
  const [description, setDescription] = useState("");
  const [businessSummary, setBusinessSummary] = useState("");
  const [knownLimitations, setKnownLimitations] = useState("");
  const [sourceBranch, setSourceBranch] = useState("");
  const [releaseBranch, setReleaseBranch] = useState("");
  const [gitTag, setGitTag] = useState("");
  const [buildNumber, setBuildNumber] = useState("");
  const [artifactVersion, setArtifactVersion] = useState("");
  const [pipelineUrl, setPipelineUrl] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  const environments = useQuery({
    queryKey: ["release-environments"],
    queryFn: () => api.get<EnvironmentView[]>("/api/v1/environments"),
    enabled: open,
  });

  const employees = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    setReleaseName(release.releaseName);
    setVersion(release.version);
    setReleaseType(release.releaseType);
    setEnvironmentId(release.targetEnvironment?.id ?? "");
    setRiskLevel(release.riskLevel ?? "MEDIUM");
    setPriority(release.priority ?? "MEDIUM");
    setPeople({
      releaseOwnerId: release.releaseOwner?.id ?? "",
      releaseManagerId: release.releaseManager?.id ?? "",
      technicalLeadId: release.technicalLead?.id ?? "",
      qaLeadId: release.qaLead?.id ?? "",
      deploymentOwnerId: release.deploymentOwner?.id ?? "",
    });
    setDescription(release.description ?? "");
    setBusinessSummary(release.businessSummary ?? "");
    setKnownLimitations(release.knownLimitations ?? "");
    setSourceBranch(release.sourceBranch ?? "");
    setReleaseBranch(release.releaseBranch ?? "");
    setGitTag(release.gitTag ?? "");
    setBuildNumber(release.buildNumber ?? "");
    setArtifactVersion(release.artifactVersion ?? "");
    setPipelineUrl(release.pipelineUrl ?? "");
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, release]);

  const blank = (value: string) => (value.trim() === "" ? null : value.trim());
  const person = (key: PeopleKey) => (people[key] === "" ? null : people[key]);

  const environmentChanged = (release.targetEnvironment?.id ?? "") !== environmentId;
  const chosen = (environments.data ?? []).find((row) => row.id === environmentId) ?? null;

  const submit = () =>
    void run(
      () =>
        api.patch(`/api/v1/releases/${release.id}`, {
          releaseName: releaseName.trim(),
          version: version.trim(),
          releaseType,
          description: blank(description),
          businessSummary: blank(businessSummary),
          releaseObjective: release.releaseObjective,
          businessImpact: release.businessImpact,
          customerImpact: release.customerImpact,
          knownLimitations: blank(knownLimitations),
          releaseOwnerId: person("releaseOwnerId"),
          releaseManagerId: person("releaseManagerId"),
          technicalLeadId: person("technicalLeadId"),
          qaLeadId: person("qaLeadId"),
          deploymentOwnerId: person("deploymentOwnerId"),
          riskLevel,
          priority,
          targetEnvironmentId: environmentId === "" ? null : environmentId,
          sourceBranch: blank(sourceBranch),
          releaseBranch: blank(releaseBranch),
          gitTag: blank(gitTag),
          buildNumber: blank(buildNumber),
          artifactVersion: blank(artifactVersion),
          pipelineUrl: blank(pipelineUrl),
          lockVersion: release.lockVersion,
        }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Edit release"
      subtitle={`${release.version} — ${release.releaseName}`}
      width="lg"
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={releaseName.trim() === "" || version.trim() === ""}
            loading={saving}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Version" required hint="Nothing is parsed.">
            <Input value={version} onChange={(event) => setVersion(event.target.value)} />
          </Field>
          <Field label="Name" required>
            <Input
              value={releaseName}
              onChange={(event) => setReleaseName(event.target.value)}
            />
          </Field>
        </div>

        <Field label="Type" hint="Drives which approvals and tests apply.">
          <Select
            value={releaseType}
            onChange={(event) => setReleaseType(event.target.value as ReleaseType)}
          >
            {TYPES.map((type) => (
              <option key={type} value={type}>
                {RELEASE_TYPE_LABELS[type]}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Going to"
          hint="What readiness is judged against. Each environment decides what it requires."
        >
          <Select
            value={environmentId}
            onChange={(event) => setEnvironmentId(event.target.value)}
          >
            <option value="">Not decided yet</option>
            {(environments.data ?? []).map((environment) => (
              <option key={environment.id} value={environment.id}>
                {environment.name}
                {environment.production ? " (production)" : ""}
              </option>
            ))}
          </Select>
        </Field>

        {environmentChanged && (
          <InlineAlert tone={chosen?.production ? "warning" : "info"}>
            {chosen
              ? `This release will be re-judged against what ${chosen.name} requires`
                + (chosen.production ? " — which is everything." : ".")
              : "With no destination the release is held to every gate."}
          </InlineAlert>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Risk" hint="How much could go wrong.">
            <Select
              value={riskLevel}
              onChange={(event) => setRiskLevel(event.target.value as Priority)}
            >
              {PRIORITIES.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
          </Field>
          <Field label="Priority" hint="How much it matters that it ships.">
            <Select
              value={priority}
              onChange={(event) => setPriority(event.target.value as Priority)}
            >
              {PRIORITIES.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
          </Field>
        </div>

        <div>
          <p className="mb-2 text-sm font-medium text-ink-900">People</p>
          <p className="mb-2 text-xs text-ink-500">
            These are who the release notifies. Nobody is told about their own action, and each
            person is told once however many of these they hold.
          </p>
          <div className="space-y-3">
            {PEOPLE.map(([key, label, hint]) => (
              <Field key={key} label={label} hint={hint}>
                <Select
                  value={people[key] ?? ""}
                  onChange={(event) =>
                    setPeople((now) => ({ ...now, [key]: event.target.value }))
                  }
                >
                  <option value="">Nobody</option>
                  {(employees.data?.content ?? []).map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.fullName}
                    </option>
                  ))}
                </Select>
              </Field>
            ))}
          </div>
        </div>

        <Field label="What this release is for" hint="Shown at the top of the release notes.">
          <Textarea
            rows={3}
            value={businessSummary}
            onChange={(event) => setBusinessSummary(event.target.value)}
          />
        </Field>

        <Field label="Description">
          <Textarea
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>

        <Field
          label="Known limitations"
          hint="What it will not do. Better said here than discovered by a customer."
        >
          <Textarea
            rows={2}
            value={knownLimitations}
            onChange={(event) => setKnownLimitations(event.target.value)}
          />
        </Field>

        <div>
          <p className="mb-2 text-sm font-medium text-ink-900">Build</p>
          <p className="mb-2 text-xs text-ink-500">
            Recorded by hand until source control is connected. Nothing here is fetched or
            verified — it is so somebody can find the code and the pipeline run afterwards.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Source branch">
              <Input
                value={sourceBranch}
                placeholder="develop"
                onChange={(event) => setSourceBranch(event.target.value)}
              />
            </Field>
            <Field label="Release branch">
              <Input
                value={releaseBranch}
                placeholder="release/2.1"
                onChange={(event) => setReleaseBranch(event.target.value)}
              />
            </Field>
            <Field label="Git tag">
              <Input
                value={gitTag}
                placeholder="v2.1.0"
                onChange={(event) => setGitTag(event.target.value)}
              />
            </Field>
            <Field label="Build number">
              <Input
                value={buildNumber}
                placeholder="1042"
                onChange={(event) => setBuildNumber(event.target.value)}
              />
            </Field>
            <Field label="Artifact version">
              <Input
                value={artifactVersion}
                placeholder="2.1.0-build.42"
                onChange={(event) => setArtifactVersion(event.target.value)}
              />
            </Field>
            <Field label="Pipeline">
              <Input
                value={pipelineUrl}
                placeholder="https://ci.example.com/run/1042"
                onChange={(event) => setPipelineUrl(event.target.value)}
              />
            </Field>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
